import { Component, inject, Input, OnInit } from '@angular/core';
import { MDBBootstrapModulesPro } from 'ng-uikit-pro-standard';
import { FormControl } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';

import { mapAsBody } from '../../../shared/util/request-util';
import { BaryTitleComponent } from '../../../shared/components/bary-title/bary-title.component';

import { OptionsTypeSelector } from '../../../../models/options-type-selector';
import { TransportExtraConditionControls } from '../../../../models/transport-extra-condition.controls';
import { TemperatureEnum } from '../../../../models/enums/temperature.enum';
import { IGenericBaryFormGroup } from '../../../../models/forms/GenericBaryFormGroup';
import { TransportFormGroup } from '../../../../models/forms/TransportFormGroup';

import { RefAdrExtendedService } from '../../../../services/ref-adr-extended.service';
import { BaryFormFactoryService } from '../../../../services/bary-form-factory.service';
import { BaryModalService } from '../../../../services/bary-modal.service';
import { BaryButtonComponent, BaryInputNumberComponent, BaryOptionsSelectorComponent } from '../../../shared/components';

import { TranslateDirective } from '../../../../directives/translate.directive';
import { BaryIconComponent } from '../../../shared/components/bary-icon/bary-icon.component';
import { FaDirectiveEnum } from '../../../../models/enums/fa-directive.enum';

@Component({
  selector: 'jhi-transport-extra-condition-form[transportForm]',
  templateUrl: './transport-extra-condition-form.component.html',
  standalone: true,
  imports: [
    BaryButtonComponent,
    TranslateModule,
    BaryTitleComponent,
    MDBBootstrapModulesPro,
    BaryOptionsSelectorComponent,
    BaryInputNumberComponent,
    TranslateDirective,
    CommonModule,
    BaryIconComponent,
  ],
  styleUrls: ['./transport-extra-condition-form.component.scss'],
})
export class TransportExtraConditionFormComponent implements OnInit {
  readonly #refAdrService = inject(RefAdrExtendedService);
  readonly #baryFormFactoryService = inject(BaryFormFactoryService);
  readonly #baryModalService = inject(BaryModalService);

  @Input() transportForm!: TransportFormGroup;
  refADRUNOptions: { label: string; value: string }[] = [];

  public readonly DEFAULT_COOL_TEMP = 4;
  public readonly DEFAULT_FROZEN_TEMP = -20;
  public readonly FAS = FaDirectiveEnum.FAS;

  public controlledTemperatureOptions = OptionsTypeSelector.CONTROLLED_TEMPERATURE;
  public controlledTemperatureTypeControl = new FormControl(TemperatureEnum.COOL);

  public ngOnInit(): void {
    this.initExtraConditions();
    this.switchToOtherType();

    this.#refAdrService
      .queryUNCode()
      .pipe(mapAsBody)
      .subscribe(it => {
        this.refADRUNOptions = it
          .sort((a: number, b: number) => a - b)
          .map(el => ({
            label: el.toString(),
            value: el.toString(),
          }));
      });
  }

  public initExtraConditions(): void {
    if (this.transportForm.controls.isControlledTemperature.value) {
      if (this.transportForm.controls.transportExtraConditions.length === 0) {
        this.transportForm.addTransportExtraCondition(this.#baryFormFactoryService, false);
      }
      const extraCondition = this.transportForm.controls.transportExtraConditions[0];

      if (extraCondition.controls.controlledTemperature.value != null) {
        const value: number = this.transportForm.controls.transportExtraConditions[0].controls.controlledTemperature.value;
        this.adapteIconWithControlledTemperatureValue(value);
      } else {
        // #898:RG3
        this.transportForm.controls.transportExtraConditions[0].controls.controlledTemperature.setValue(this.DEFAULT_COOL_TEMP);
      }

      extraCondition.controls.controlledTemperature.valueChanges.pipe(debounceTime(300)).subscribe(value => {
        this.adapteIconWithControlledTemperatureValue(value);
      });

      // #898:RG1 #898:RG2
      this.controlledTemperatureTypeControl.valueChanges.subscribe(temperature => {
        if (temperature === TemperatureEnum.COOL) {
          extraCondition.controls.controlledTemperature.setValue(this.DEFAULT_COOL_TEMP, { emitEvent: false });
        } else {
          extraCondition.controls.controlledTemperature.setValue(this.DEFAULT_FROZEN_TEMP, { emitEvent: false });
        }
      });
    }
  }

  private adapteIconWithControlledTemperatureValue(value: number): void {
    if (value > 0) {
      this.controlledTemperatureTypeControl.setValue(TemperatureEnum.COOL, { emitEvent: false });
    } else {
      this.controlledTemperatureTypeControl.setValue(TemperatureEnum.FROZEN, { emitEvent: false });
    }
  }

  addNewADR(): void {
    this.#baryModalService
      .openAddOrUpdateADR(this.refADRUNOptions, this.#baryFormFactoryService.makeTransportExtraConditionsForm(this.transportForm.controls.isControlledTemperature.value))
      .action()
      .subscribe(action => {
        if (action.submit && action.value) {
          this.transportForm.addTransportExtraConditionForm(action.value);
        }
      });
  }

  private switchToOtherType(): void {
    this.transportForm.controls.isHazardousMaterial.valueChanges.subscribe(newValue => {
      this.transportForm.resetTransportExtraCondition();
    });

    this.transportForm.controls.isControlledTemperature.valueChanges.subscribe(newValue => {
      this.transportForm.resetTransportExtraCondition();
      if (newValue) {
        this.transportForm.addTransportExtraCondition(this.#baryFormFactoryService, false);
        this.initExtraConditions();
      }
    });
  }

  removeExtraCondition(index: number): void {
    this.transportForm.removeTransportExtraCondition(index);
  }

  editADR(transportExtraCondition: IGenericBaryFormGroup<TransportExtraConditionControls>): void {
    this.#baryModalService
      .openAddOrUpdateADR(
        this.refADRUNOptions,
        this.#baryFormFactoryService.makeTransportExtraConditionsForm(this.transportForm.controls.isHazardousMaterial.value, transportExtraCondition.value())
      )
      .action()
      .subscribe(action => {
        if (action.submit && action.value) {
          transportExtraCondition.getAngularForm().patchValue(action.value.form.value);
        }
      });
  }

  getAdrLine(transportExtraConditionForm: IGenericBaryFormGroup<TransportExtraConditionControls>): string {
    let line = '';
    line +=
      'ADR ONU (' +
      transportExtraConditionForm.controls.adrCodeUN.value +
      ') en ' +
      transportExtraConditionForm.controls.adrPackingNumber.value +
      ' ' +
      transportExtraConditionForm.controls.adrPacking.value +
      '(s)';

    if (transportExtraConditionForm.controls.adrPackingGroup.value) {
      line += ' (' + transportExtraConditionForm.controls.adrPackingGroup.value + ')';
    }
    if (transportExtraConditionForm.controls.adrWasteBsdCode.value) {
      line += ' - BSD ' + transportExtraConditionForm.controls.adrWasteBsdCode.value;
    }

    return line;
  }
}
